import { type BuildFacetGraphParams, type ExplodeFlow, NodeRole } from "../../types";
import { titleEdge } from "./graphEdges";
import {
    COLUMN_GAP,
    FIELD_FOCUS_TOP_VALUES,
    NODE_WIDTH,
    OVERLAY_Z,
    ROOT_Y,
    ZONE_LEFT_OFFSET,
    ZONE_ROWS,
    ZONE_WIDTH,
} from "./graphLayout";
import { graphNode } from "./graphNode";
import { rowTops } from "./rowTops";

export const buildFieldFocus = ({
    categories,
    categoryField,
    categoryLabel,
    rootLabel,
}: {
    categories: BuildFacetGraphParams["categories"];
    categoryField: string;
    categoryLabel: string;
    rootLabel: string;
}): ExplodeFlow => {
    const stepX = NODE_WIDTH + COLUMN_GAP;
    const rootNode = graphNode("root", 0, ROOT_Y, {
        label: rootLabel,
        role: NodeRole.ROOT,
        selection: [],
    });
    const fieldNode = graphNode(
        "field-focus",
        stepX,
        ROOT_Y,
        {
            label: categoryLabel,
            role: NodeRole.TITLE,
            title: true,
            focused: true,
            count: categories.length,
            selection: [],
        },
        OVERLAY_Z,
    );

    const centerX = stepX;
    const tops = rowTops(ROOT_Y);
    const topValues = categories.slice(0, FIELD_FOCUS_TOP_VALUES);
    const branchNodes = topValues.map((bucket, index) => {
        const col = Math.floor(index / ZONE_ROWS);
        const row = index % ZONE_ROWS;
        return graphNode(
            `fv-${index}`,
            centerX + ZONE_LEFT_OFFSET + col * ZONE_WIDTH,
            tops[row],
            {
                label: bucket.value,
                count: bucket.count,
                role: NodeRole.GROUP,
                selection: [{ field: categoryField, value: bucket.value }],
            },
            OVERLAY_Z,
        );
    });

    const showAllNode =
        categories.length > topValues.length
            ? [
                  graphNode(
                      "fv-all",
                      centerX +
                          ZONE_LEFT_OFFSET +
                          Math.floor(topValues.length / ZONE_ROWS) * ZONE_WIDTH,
                      tops[topValues.length % ZONE_ROWS],
                      {
                          label: `…show all ${categories.length.toLocaleString()} values`,
                          role: NodeRole.GROUP,
                          selection: [],
                          muted: true,
                          overlay: { field: categoryField, label: categoryLabel },
                          overlayPage: 0,
                      },
                      OVERLAY_Z,
                  ),
              ]
            : [];

    const branchAndAll = [...branchNodes, ...showAllNode];
    const rootEdge = titleEdge("root", "field-focus", "e-root-field");
    const branchEdges = branchAndAll.map((branch, index) =>
        titleEdge("field-focus", branch.id, `e-fv-${index}`),
    );

    return {
        nodes: [rootNode, fieldNode, ...branchAndAll],
        edges: [rootEdge, ...branchEdges],
    };
};
